import { Collapse, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import type { ProcessImpact } from "../../api";

const { Text } = Typography;

type Props = {
  impact: ProcessImpact;
  hasUnpublishedChanges: boolean;
  pendingChanges: string[];
};

/**
 * Who out there would notice a change to this process.
 *
 * Publishing never disturbs a permit already in progress — it keeps the version
 * it was pinned to — but an admin about to change the rules still deserves to
 * know how many permits are live and on which version. Deliberately phrased as
 * information rather than a warning: an alarm here would just teach people to
 * ignore it.
 */
const ImpactSummary = ({
  impact,
  hasUnpublishedChanges,
  pendingChanges,
}: Props) => {
  const { t } = useTranslation();

  if (!impact || impact.total === 0) {
    return (
      <Text type="secondary">
        {t("No permits use this template yet, so nothing is affected.")}
      </Text>
    );
  }

  return (
    <Space direction="vertical" size={6} style={{ width: "100%" }}>
      <Space wrap size={8}>
        <Text>
          {t("{{n}} permit(s) still in progress").replace(
            "{{n}}",
            String(impact.in_flight)
          )}
        </Text>
        <Text type="secondary">
          {t("of {{n}} in total").replace("{{n}}", String(impact.total))}
        </Text>
      </Space>

      <Space wrap size={6}>
        {impact.by_version.map((row) => (
          <Tag key={String(row.version)}>
            {row.version == null
              ? t("Started before versioning")
              : `${t("Version")} ${row.version}`}
            {" · "}
            {row.in_flight}/{row.count}
          </Tag>
        ))}
      </Space>

      <Text type="secondary">
        {t(
          "Permits already in progress keep the process they started on. Publishing only affects permits created from now on."
        )}
      </Text>

      {hasUnpublishedChanges && (
        <Collapse
          size="small"
          ghost
          items={[
            {
              key: "pending",
              label: t("What publishing would change"),
              children:
                pendingChanges.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {pendingChanges.map((change, i) => (
                      <li key={i}>
                        <Text type="secondary">{change}</Text>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Text type="secondary">
                    {t(
                      "Nothing in the published layout. The edits are to settings that apply immediately, such as who fills a section."
                    )}
                  </Text>
                ),
            },
          ]}
        />
      )}
    </Space>
  );
};

export default ImpactSummary;
